/**
 * API calls for managing students and groups.
 * Adapts the backend contract to frontend types.
 */

import { api } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type {
  ManagedStudent,
  StudentsListResponse,
  ManagedGroup,
  GroupsListResponse,
  GradeOption,
  PaginationMeta,
  TutorsListResponse,
} from 'api/arcMosaico/types'

/**
 * Guard for 401 responses. The axios interceptor does NOT reject on 401
 * (it returns the response directly), so we must check status manually.
 */
function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

// ============================================================================
// Adapters — transform backend shape → frontend types
// ============================================================================

interface RawStudent {
  id: string
  firstName: string
  lastName: string
  username: string
  email: string | null
  status: 'active' | 'inactive'
  inviteStatus: string
  avatarUrl: string | null
  grade: GradeOption | null
  groups?: Array<{ id: string; name: string }>
  can: {
    editProfile: boolean
    resetPassword: boolean
    viewProfile: boolean
    viewDisc: boolean
    editLegalGuardians: boolean
    remove: boolean
  }
  createdAt: string
}

function adaptStudent(raw: RawStudent): ManagedStudent {
  return {
    id: raw.id,
    firstName: raw.firstName,
    lastName: raw.lastName,
    username: raw.username,
    email: raw.email,
    status: raw.status,
    inviteStatus: raw.inviteStatus as ManagedStudent['inviteStatus'],
    avatarUrl: raw.avatarUrl,
    grades: raw.grade ? [raw.grade] : [],
    groups: raw.groups || [],
    permissions: raw.can || {
      editProfile: false,
      resetPassword: false,
      viewProfile: false,
      viewDisc: false,
      editLegalGuardians: false,
      remove: false,
    },
    createdAt: raw.createdAt,
  }
}

// ============================================================================
// Students
// ============================================================================

export interface ListStudentsParams {
  page?: number
  limit?: number
  search?: string
  groupSchoolTermId?: string | null
  status?: string
}

export async function listStudents(
  params: ListStudentsParams
): Promise<StudentsListResponse> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.students.list, {
    params: {
      page: params.page || 1,
      limit: params.limit || 9,
      search: params.search || undefined,
      groupSchoolTermId: params.groupSchoolTermId || undefined,
      status: params.status || undefined,
    },
  })
  assertAuthenticated(response)

  const data = response.data as {
    students: RawStudent[]
    pagination: PaginationMeta
  }

  return {
    students: (data.students || []).map(adaptStudent),
    pagination: data.pagination,
  }
}

export async function updateStudent(
  id: string,
  data: {
    firstName?: string
    lastName?: string
    username?: string
    email?: string
    gradeId?: string | null
    groupSchoolTermIds?: string[]
  }
): Promise<void> {
  const response = await api.put(ARC_MOSAICO_ENDPOINTS.students.update(id), data)
  assertAuthenticated(response)
}

export async function deleteStudent(id: string): Promise<void> {
  const response = await api.delete(ARC_MOSAICO_ENDPOINTS.students.destroy(id))
  assertAuthenticated(response)
}

export interface ResetPasswordResult {
  generatedPassword: string
}

export async function resetStudentPassword(
  id: string,
  newPassword?: string
): Promise<ResetPasswordResult> {
  const response = await api.post(
    ARC_MOSAICO_ENDPOINTS.students.resetPassword(id),
    newPassword ? { newPassword } : {}
  )
  assertAuthenticated(response)
  return response.data as ResetPasswordResult
}

// ============================================================================
// Groups
// ============================================================================

export async function listGroups(limit = 100): Promise<ManagedGroup[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.groups.list, {
    params: { limit },
  })
  assertAuthenticated(response)

  const data = response.data as GroupsListResponse
  return data.groups || []
}

export async function createGroup(
  name: string,
  tutorIds?: string[],
): Promise<{ id: string; name: string }> {
  const response = await api.post(ARC_MOSAICO_ENDPOINTS.groups.create, {
    name,
    ...(tutorIds && tutorIds.length > 0 ? { tutorIds } : {}),
  })
  assertAuthenticated(response)
  return response.data as { id: string; name: string }
}

/**
 * All teachers of the selected school, for the manager's teacher picker.
 * Teachers without school management permission get 403 on this endpoint,
 * so only call it for managers.
 */
export async function listSchoolTeachers(): Promise<Array<{ id: string; name: string }>> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.tutors.list, {
    params: { page: 1, limit: 100 },
  })
  assertAuthenticated(response)

  const data = response.data as TutorsListResponse
  return (data.tutors || []).map(tutor => ({
    id: tutor.id,
    name: `${tutor.firstName} ${tutor.lastName}`.trim(),
  }))
}

export async function updateGroup(
  id: string,
  name: string
): Promise<{ id: string; name: string }> {
  const response = await api.put(ARC_MOSAICO_ENDPOINTS.groups.update(id), { name })
  assertAuthenticated(response)
  return response.data as { id: string; name: string }
}

export async function deleteGroup(id: string): Promise<void> {
  const response = await api.delete(ARC_MOSAICO_ENDPOINTS.groups.destroy(id))
  assertAuthenticated(response)
}

// ============================================================================
// Grades
// ============================================================================

export async function listGrades(): Promise<GradeOption[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.grades.list)
  assertAuthenticated(response)
  return response.data as GradeOption[]
}
